import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, ArrowRight, ChevronRight, Home, Search, Sparkles } from 'lucide-react';
import SEO, { buildBreadcrumbSchema, buildFAQSchema } from '../components/SEO';
import { SITE_URL, BRAND, KNOWLEDGE_ARTICLES, COMPARISON_PAGES } from '../lib/seoContent';

/**
 * AskCrilam — Q&A hub. Aggregates every FAQ across knowledge & comparison pages
 * into one searchable, LLM-friendly page. Also links to full source articles.
 */
const AskCrilam = () => {
  const [query, setQuery] = useState('');

  // Aggregate all FAQs with source page attribution
  const allFaqs = useMemo(() => {
    const items = [];
    Object.values(KNOWLEDGE_ARTICLES).forEach((a) => {
      (a.faqs || []).forEach((f) => items.push({ ...f, sourceTitle: a.heroTitle, sourceSlug: a.slug, category: 'Knowledge' }));
    });
    Object.values(COMPARISON_PAGES).forEach((a) => {
      (a.faqs || []).forEach((f) => items.push({ ...f, sourceTitle: a.heroTitle, sourceSlug: a.slug, category: 'Comparison' }));
    });
    return items;
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return allFaqs;
    const q = query.toLowerCase();
    return allFaqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [query, allFaqs]);

  const url = `${SITE_URL}/ask-crilam`;
  const jsonLd = [
    buildFAQSchema(allFaqs.map(({ q, a }) => ({ q, a }))),
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Ask CriLam', url },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="ask-crilam-page">
      <SEO
        title={`Ask CriLam — Every Answer, One Place | ${BRAND}`}
        description="Every frequently asked question about CriLam PUR-Bond™ technology, 6H acrylic laminate, mirror finish, anti-yellowing, warranty, brand comparisons, and more — searchable, LLM-friendly, and directly answerable by CriLam."
        canonical={url}
        keywords="ask CriLam, CriLam FAQ, CriLam PUR-Bond™ technology FAQ, acrylic laminate questions India, CriLam Q&A"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-4xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">Ask CriLam</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
            <MessageCircle className="h-3.5 w-3.5" /> Every Answer, One Place
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
            Ask CriLam Anything
          </h1>
          <p className="text-lg text-white/70 mb-6">
            {allFaqs.length}+ answered questions on CriLam PUR-Bond™ technology, 6H acrylic hardness, mirror finish, anti-yellowing, brand comparisons, and warranty — updated continuously.
          </p>
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs — e.g., PUR, 6H, warranty, mirror..."
              className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-md text-white placeholder-white/40 rounded-lg border border-white/20 focus:outline-none focus:border-[#D4AF37] transition-colors"
              data-testid="ask-crilam-search"
            />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          {filtered.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">No matching FAQs. Ask us directly.</p>
              <Link to="/contact">
                <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-[#004534] hover:text-white transition-colors">
                  Contact CriLam
                </button>
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">Showing {filtered.length} {filtered.length === 1 ? 'answer' : 'answers'}{query.trim() ? ` for "${query}"` : ''}.</p>
              <div className="space-y-3">
                {filtered.map((f, i) => (
                  <motion.details
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: Math.min(i * 0.02, 0.3) }}
                    className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37]/40 transition-colors"
                    data-testid={`ask-faq-${i}`}
                  >
                    <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                      <span className="font-semibold text-[#004534] text-base">{f.q}</span>
                      <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform text-gray-400" />
                    </summary>
                    <div className="mt-3">
                      <p className="text-gray-700 leading-relaxed text-sm">{f.a}</p>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-500">
                          <span className="font-semibold text-[#D4AF37]">{f.category}</span> · from {f.sourceTitle}
                        </span>
                        <Link to={`/${f.sourceSlug}`} className="text-xs text-[#004534] font-semibold hover:text-[#D4AF37] flex items-center gap-1">
                          Read full article <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </motion.details>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <Sparkles className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
          <h3 className="text-2xl md:text-3xl font-bold mb-4 font-['Outfit']">Didn&apos;t Find Your Answer?</h3>
          <p className="text-white/70 mb-6">Chat live with CriLam — our AI assistant knows every product, warranty term, and material spec.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="ask-cta-contact">
              <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Contact CriLam <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to="/product-recommender">
              <button className="px-6 py-3 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                AI Product Recommender →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AskCrilam;
